/**
 * Apps2Go Hub – Yleinen moduulisivu (Z1).
 *
 * Renderöidään `def`-konfiguraation pohjalta:
 *   - KPI-rivi (4 korttia)
 *   - Tabs (jos `def.tabs` annettu)
 *   - Taulukko / korttilista
 *   - Lomakedialogi Add/Edit/Delete -toimintoihin
 *
 * Käyttää back-endin geneerisiä CRUD-reittejä `/api/<def.api>`.
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Edit, Trash2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { ModuleSettingsButton } from "@/components/admin/FactoryResetDialog";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";

const eur = (v) =>
  new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

const fmt = (v, type) => {
  if (v == null || v === "") return "—";
  if (type === "currency") return eur(v);
  if (type === "datetime") return String(v).slice(0, 16).replace("T", " ");
  if (type === "date") return String(v).slice(0, 10);
  return String(v);
};

const BADGE_COLOR = {
  blue: "bg-blue-500/20 text-blue-200 border-blue-500/30",
  amber: "bg-amber-500/20 text-amber-200 border-amber-500/30",
  emerald: "bg-emerald-500/20 text-emerald-200 border-emerald-500/30",
  red: "bg-red-500/20 text-red-200 border-red-500/30",
  violet: "bg-violet-500/20 text-violet-200 border-violet-500/30",
  rose: "bg-rose-500/20 text-rose-200 border-rose-500/30",
  cyan: "bg-cyan-500/20 text-cyan-200 border-cyan-500/30",
  slate: "bg-slate-500/20 text-slate-200 border-slate-500/30",
};

function RowTable({ rows, columns, onEdit, onDelete, testidPrefix }) {
  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-white/40">
        <p className="text-sm">Ei vielä merkintöjä.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto relative z-10">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
            {columns.map((c) => (
              <th key={c.key} className="py-3 px-4">{c.label}</th>
            ))}
            <th className="py-3 px-4 w-24"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-white/5 hover:bg-white/[0.02]"
              data-testid={`${testidPrefix}-row-${row.id}`}
            >
              {columns.map((c) => (
                <td key={c.key} className="py-3 px-4">
                  {c.type === "badge" ? (
                    <Badge
                      className={`text-[10px] border ${
                        BADGE_COLOR[c.color?.[row[c.key]] || "blue"]
                      }`}
                    >
                      {row[c.key] || "—"}
                    </Badge>
                  ) : c.type === "currency" ? (
                    <span className="text-amber-300 font-mono">
                      {fmt(row[c.key], "currency")}
                    </span>
                  ) : c.type === "datetime" ? (
                    <span className="text-white/70 text-xs">
                      {fmt(row[c.key], "datetime")}
                    </span>
                  ) : c.type === "date" ? (
                    <span className="text-white/70 text-xs">
                      {fmt(row[c.key], "date")}
                    </span>
                  ) : c.type === "progress" ? (
                    <div className="w-32">
                      <div className="flex items-center justify-between text-[10px] text-white/60 mb-0.5">
                        <span>{row[c.key] || 0}%</span>
                      </div>
                      <div className="w-full bg-white/5 rounded-full h-1.5">
                        <div
                          className="bg-gradient-to-r from-amber-400 to-purple-500 h-1.5 rounded-full"
                          style={{ width: `${row[c.key] || 0}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <span className="text-white/90">
                      {fmt(row[c.key])}
                      {c.suffix || ""}
                    </span>
                  )}
                </td>
              ))}
              <td className="py-3 px-4">
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="w-7 h-7"
                    onClick={() => onEdit(row)}
                    data-testid={`${testidPrefix}-edit-${row.id}`}
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="w-7 h-7 text-red-300"
                    onClick={() => onDelete(row.id)}
                    data-testid={`${testidPrefix}-delete-${row.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GenericModulePage({ def }) {
  const Icon = def.icon;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [activeTab, setActiveTab] = useState(def.tabs?.[0]?.value || "all");

  const testidPrefix = def.collection || "module";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(def.api);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [def.api]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const data = { ...form };
      if (data.id) {
        await api.put(`${def.api}/${data.id}`, data);
        toast.success("Päivitetty");
      } else {
        await api.post(def.api, data);
        toast.success("Lisätty");
      }
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko?")) return;
    try {
      await api.delete(`${def.api}/${id}`);
      toast.success("Poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filteredAll = useMemo(() => {
    if (!q) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some(
        (v) => v && String(v).toLowerCase().includes(needle),
      ),
    );
  }, [rows, q]);

  const newForm = () => {
    const f = {};
    (def.fields || []).forEach((fld) => {
      if (fld.default !== undefined) f[fld.key] = fld.default;
    });
    setForm(f);
  };

  // KPI calc
  const kpiValues = (def.kpis || []).map((kpi) => kpi.calc(rows));

  return (
    <div className="space-y-6" data-testid={`module-${testidPrefix}-page`}>
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-xl bg-gradient-to-br ${def.hue} flex items-center justify-center shadow-lg`}
          >
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
              Moduuli
            </p>
            <h1 className="text-3xl font-display gradient-text-luxe">
              {def.label}
            </h1>
            <p className="text-white/60 text-sm mt-1">{def.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ModuleSettingsButton
            moduleKey={def.key}
            moduleLabel={def.label}
            onAfterReset={load}
          />
          <Button
            onClick={newForm}
            className="btn-luxe inline-flex items-center gap-2"
            data-testid={`${testidPrefix}-add`}
          >
            <Plus className="w-4 h-4" /> Uusi {def.entityLabel || "tietue"}
          </Button>
        </div>
      </header>

      {/* KPI strip */}
      {def.kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {def.kpis.map((kpi, i) => {
            const KpiIcon = kpi.icon;
            return (
              <div
                key={i}
                className="stat-block"
                data-testid={`${testidPrefix}-kpi-${i}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">
                    {kpi.label}
                  </p>
                  {KpiIcon && (
                    <KpiIcon
                      className={`w-4 h-4 ${
                        kpi.accent === "emerald"
                          ? "text-emerald-300"
                          : kpi.accent === "amber"
                          ? "text-amber-300"
                          : kpi.accent === "violet"
                          ? "text-violet-300"
                          : kpi.accent === "red"
                          ? "text-red-300"
                          : "text-white/50"
                      }`}
                    />
                  )}
                </div>
                <p
                  className={`text-2xl font-display font-bold mt-1 ${
                    kpi.accent === "emerald"
                      ? "text-emerald-300"
                      : kpi.accent === "amber"
                      ? "text-amber-300"
                      : kpi.accent === "violet"
                      ? "text-violet-300"
                      : kpi.accent === "red"
                      ? "text-red-300"
                      : "text-white"
                  }`}
                >
                  {kpiValues[i]}
                </p>
                {kpi.hint && (
                  <p className="text-[10px] text-white/40 mt-1">{kpi.hint}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="topbar-search flex items-center gap-2 px-3 py-2 max-w-md">
        <Search className="w-4 h-4 text-white/40" />
        <input
          type="text"
          placeholder={`Hae ${def.entityLabel?.toLowerCase() || "tietuetta"}...`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="bg-transparent text-sm text-white/90 outline-none flex-1"
          data-testid={`${testidPrefix}-search`}
        />
      </div>

      {/* Tabs / List */}
      {(() => {
        // Lisää Henkilökunta-tab automaattisesti kaikkiin moduuleihin (Phase 17B)
        const baseTabs = def.tabs && def.tabs.length > 0 ? def.tabs : null;
        const STAFF_TAB = { value: "__staff__", label: "Henkilökunta", isStaff: true };
        const tabs = baseTabs ? [...baseTabs, STAFF_TAB] : [
          { value: "all", label: "Kaikki", filter: null },
          STAFF_TAB,
        ];
        return (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/[0.04] border border-white/5">
            {tabs.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200"
                data-testid={`${testidPrefix}-tab-${t.value}`}
              >
                {t.isStaff && <Users className="w-3.5 h-3.5 mr-1.5" />}
                {t.label}
                {t.badgeCalc && (
                  <span className="ml-2 text-[10px] bg-white/10 px-1.5 py-0.5 rounded">
                    {t.badgeCalc(rows)}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => {
            if (t.isStaff) {
              return (
                <TabsContent key={t.value} value={t.value} className="mt-4">
                  <ModuleStaffTab moduleKey={def.key} moduleLabel={def.label} />
                </TabsContent>
              );
            }
            const tabRows = t.filter
              ? filteredAll.filter(t.filter)
              : filteredAll;
            const cols = t.columns || def.columns;
            return (
              <TabsContent key={t.value} value={t.value} className="mt-4">
                <div className="glass-card p-0 overflow-hidden">
                  {loading ? (
                    <div className="p-8 flex items-center gap-2 text-white/40">
                      <Loader2 className="w-5 h-5 animate-spin" /> Ladataan...
                    </div>
                  ) : t.render ? (
                    t.render(tabRows, { rows, setForm })
                  ) : (
                    <RowTable
                      rows={tabRows}
                      columns={cols}
                      onEdit={setForm}
                      onDelete={remove}
                      testidPrefix={testidPrefix}
                    />
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
        );
      })()}

      {/* Form dialog */}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {form?.id
                ? `Muokkaa ${def.entityLabel?.toLowerCase() || ""}`
                : `Uusi ${def.entityLabel?.toLowerCase() || "tietue"}`}
            </DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              {(def.fields || []).map((fld) => (
                <div key={fld.key} className={fld.col === 2 ? "col-span-2" : ""}>
                  <Label className="text-xs">
                    {fld.label}
                    {fld.required && (
                      <span className="text-red-400 ml-1">*</span>
                    )}
                  </Label>
                  {fld.type === "select" ? (
                    <select
                      value={form[fld.key] || fld.default || ""}
                      onChange={(e) =>
                        setForm({ ...form, [fld.key]: e.target.value })
                      }
                      className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                      data-testid={`${testidPrefix}-field-${fld.key}`}
                    >
                      <option value="">—</option>
                      {(fld.options || []).map((o) => {
                        const value = typeof o === "string" ? o : o.value;
                        const label = typeof o === "string" ? o : o.label;
                        return (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  ) : fld.type === "textarea" ? (
                    <textarea
                      value={form[fld.key] || ""}
                      onChange={(e) =>
                        setForm({ ...form, [fld.key]: e.target.value })
                      }
                      className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                      placeholder={fld.placeholder || ""}
                      data-testid={`${testidPrefix}-field-${fld.key}`}
                    />
                  ) : (
                    <Input
                      type={
                        fld.type === "currency" || fld.type === "number"
                          ? "number"
                          : fld.type || "text"
                      }
                      step={fld.type === "currency" ? "0.01" : undefined}
                      value={form[fld.key] ?? ""}
                      onChange={(e) => {
                        const v =
                          fld.type === "currency" || fld.type === "number"
                            ? e.target.value === ""
                              ? ""
                              : parseFloat(e.target.value)
                            : e.target.value;
                        setForm({ ...form, [fld.key]: v });
                      }}
                      placeholder={fld.placeholder || ""}
                      data-testid={`${testidPrefix}-field-${fld.key}`}
                    />
                  )}
                </div>
              ))}
              <div className="col-span-2 flex gap-2 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setForm(null)}
                  className="flex-1"
                  data-testid={`${testidPrefix}-cancel`}
                >
                  Peruuta
                </Button>
                <Button
                  onClick={save}
                  className="btn-luxe flex-1"
                  data-testid={`${testidPrefix}-save`}
                >
                  Tallenna
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
